"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useUserSession } from '@/hooks/use-user-session';
import { signOut } from 'firebase/auth';
import { authenticateAndCreateSession } from '@/utils/auth-helpers';
import { useLocale, useTranslations } from 'next-intl';
import { doc, getDoc } from 'firebase/firestore';
import { db, firebaseAuth as auth } from '@/libs/firebase/config';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X, UserCircle2, LogOut, ChevronDown } from 'lucide-react';
import Image from 'next/image';
import { usePremiumStatus } from '@/hooks/use-premium-status';
import { resolvePremiumStatus } from '@/utils/resolve-premium-status';
import { Button } from '@/components/ui/Button';
import Spinner from '@/components/ui/Spinner';
import { useCSRF } from '@/contexts/csrf-context';
import OracleWisdomsDropDown from './OracleWisdomsDropDown';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuth } from '@/contexts/AuthContext';
import { NotificationBell } from '@/components/notifications/NotificationBell';

const checkPremiumAndRedirect = async (user, setLoading, router, locale) => {
  if (!user) return;
  setLoading(true);
  try {
    const userDocRef = doc(db, 'users', user.uid);
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();
      const premiumStatus = resolvePremiumStatus(userData);
      
      if (premiumStatus.isPremium) {
        router.push(`/${locale}/temple`);
        return;
      }
    }

    alert("Access Denied. You do not have active premium access. Please use the 'Chat with the Oracle' button to purchase access.");
    setLoading(false);
  } catch (error) {
    console.error('Error checking premium status:', error);
    alert('An error occurred while checking your access. Please try again.');
    setLoading(false);
  }
};

export default function Header({ onToggleSidebar, className, isTransparent }) {
  const t = useTranslations('common.header');
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const { csrfToken, refreshCSRF } = useCSRF();
  const userSessionId = useUserSession();
  const { firebaseUser, userProfile } = useAuth();
  const { isPremium, loading: premiumLoading } = usePremiumStatus();

  const [hasScrolled, setHasScrolled] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [currentHash, setCurrentHash] = useState('');
  const [isProcessingSignIn, setIsProcessingSignIn] = useState(false);
  const [isProcessingSignOut, setIsProcessingSignOut] = useState(false);
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const [isFloatingCTAVisible, setIsFloatingCTAVisible] = useState(false);
  const [ctaIdle, setCtaIdle] = useState(false);
  const avatarMenuRef = useRef(null);
  const avatarTriggerRef = useRef(null);
  const mobileNavRef = useRef(null);
  const idleTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setHasScrolled(window.scrollY > 10);

    if (isTransparent) {
      window.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => window.removeEventListener('scroll', handleScroll);
    }

    const handleHashChange = () => setCurrentHash(window.location.hash);
    window.addEventListener('hashchange', handleHashChange);
    setCurrentHash(window.location.hash);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [isTransparent]);

  useEffect(() => {
    if (userSessionId === null) {
      setIsProcessingSignIn(false);
      setIsProcessingSignOut(false);
      setIsAvatarMenuOpen(false);
    }
  }, [userSessionId]);

  const closeMobileNav = useCallback(() => {
    setIsMobileNavOpen(false);
    if (typeof document !== 'undefined') {
      document.body.style.removeProperty('overflow');
    }
  }, []);

  const openMobileNav = useCallback(() => {
    setIsMobileNavOpen(true);
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
    }
  }, []);

  const handleMobileMenuClick = () => {
    if (onToggleSidebar) {
      onToggleSidebar();
    } else {
      if (isMobileNavOpen) {
        closeMobileNav();
      } else {
        openMobileNav();
      }
    }
  };

  const handleSignIn = async () => {
    setIsProcessingSignIn(true);
    try {
      const result = await authenticateAndCreateSession(refreshCSRF);
      
      if (result.success && result.user) {
        router.push(`/${locale}/temple`);
        setIsProcessingSignIn(false);
        return;
      } else if (!result.user && !result.error) {
        // Redirect flow initiated (mobile)
        setIsProcessingSignIn(false);
        return;
      } else if (result.error) {
        // Handle specific error codes
        if (result.error.code === 'popup-closed') {
          console.log('Sign-in popup closed by user.');
        } else if (result.error.code === 'popup-cancelled') {
          console.log('Sign-in popup request cancelled.');
        } else if (result.error.code === 'redirect-cancelled') {
          console.log('Sign-in redirect operation was cancelled or failed.');
        } else {
          console.error('Error during authentication:', result.error);
          alert(result.error.message || 'An error occurred during the sign-in process. Please try again.');
        }
        setIsProcessingSignIn(false);
      }
    } catch (error) {
      console.error('Unexpected error during sign-in:', error);
      alert('An error occurred during the sign-in process. Please try again.');
      setIsProcessingSignIn(false);
    }
  };

  const handleSignOut = async () => {
    setIsProcessingSignOut(true);
    try {
      await signOut(auth);
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });

      if (!response.ok) {
        throw new Error('Failed to destroy server session.');
      }

      window.location.href = `/${locale}`;
    } catch (error) {
      console.error('Error during sign out:', error);
      alert('Failed to sign out. Please try again.');
      setIsProcessingSignOut(false);
    }
  };

  useEffect(() => {
    const handlePathChange = () => {
      setIsAvatarMenuOpen(false);
      closeMobileNav();
    };
    handlePathChange();
  }, [pathname, closeMobileNav]);

  useEffect(() => () => {
    if (typeof document !== 'undefined') {
      document.body.style.removeProperty('overflow');
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        closeMobileNav();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [closeMobileNav]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const activateIdle = () => setCtaIdle(true);

    const handleActivity = () => {
      setCtaIdle(false);
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
      idleTimeoutRef.current = window.setTimeout(activateIdle, 12000);
    };

    handleActivity();
    const events = ['pointermove', 'mousedown', 'touchstart', 'keydown', 'scroll'];
    events.forEach((eventName) => window.addEventListener(eventName, handleActivity, { passive: true }));

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, handleActivity));
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateFloatingCTA = () => {
      const shouldShow = window.innerWidth < 768 && window.scrollY > 200 && !isMobileNavOpen && !userSessionId;
      setIsFloatingCTAVisible(shouldShow);
    };

    updateFloatingCTA();
    const options = { passive: true };
    window.addEventListener('scroll', updateFloatingCTA, options);
    window.addEventListener('resize', updateFloatingCTA, options);

    return () => {
      window.removeEventListener('scroll', updateFloatingCTA, options);
      window.removeEventListener('resize', updateFloatingCTA, options);
    };
  }, [isMobileNavOpen, userSessionId]);

  useEffect(() => {
    if (!isMobileNavOpen) {
      return undefined;
    }

    const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const dialogNode = mobileNavRef.current;

    if (!dialogNode) {
      return undefined;
    }

    const focusableElements = Array.from(dialogNode.querySelectorAll(focusableSelector));
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    firstElement?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Tab') {
        if (focusableElements.length === 0) {
          return;
        }

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement?.focus();
          }
        } else if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      } else if (event.key === 'Escape') {
        closeMobileNav();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileNavOpen, closeMobileNav]);

  useEffect(() => {
    if (!isAvatarMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (!avatarMenuRef.current || !avatarTriggerRef.current) return;
      if (
        !avatarMenuRef.current.contains(event.target) &&
        !avatarTriggerRef.current.contains(event.target)
      ) {
        setIsAvatarMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsAvatarMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isAvatarMenuOpen]);

  const accountMenuLabel = t('account.menuLabel');
  const accountProfileLabel = t('account.profile');
  const accountHistoryLabel = t('account.history');
  const accountLogoutLabel = t('account.logout');
  const accountAnonymousLabel = t('account.anonymous');
  const mobileNavLabel = t('mobileNavLabel');

  const avatarDisplayName = userProfile?.name?.trim() ? userProfile.name : (firebaseUser?.displayName || accountAnonymousLabel);
  const avatarEmail = userProfile?.email || firebaseUser?.email || '';

  const headerIsOpaque = !isTransparent || hasScrolled;
  const baseHeaderClasses = `fixed top-0 left-0 right-0 z-50 font-sans transition-colors duration-300 ${headerIsOpaque ? 'border-b border-[#6A3683]/30 backdrop-blur-sm bg-[#050316]/90' : 'border-b border-transparent'}`;
  const containerClasses = 'container mx-auto flex w-full items-center px-3 md:px-4 lg:px-5 py-3 md:py-4';
  const navWrapperClasses = 'hidden md:flex md:ml-1 lg:ml-2';
  const navPillClasses = 'flex items-center space-x-1.5 md:space-x-2 lg:space-x-3 rounded-full border border-[#6A3683]/40 bg-[#070219]/70 px-2 md:px-3 lg:px-4 py-1.5 md:py-2 shadow-[0_12px_30px_rgba(10,3,30,0.35)] backdrop-blur';
  const navListClasses = 'flex items-center space-x-1.5 md:space-x-2 lg:space-x-2.5';
  const navLinkClasses = 'relative flex items-center whitespace-nowrap px-1 py-2 text-sm font-medium text-[#FBFBFE] transition-colors duration-300 hover:text-[#D4B6FF]';
  const loginLinkClasses = 'text-sm font-medium text-[#FBFBFE] transition-colors duration-300 hover:text-[#D4B6FF] underline-offset-4 hover:underline whitespace-nowrap';
  const buttonHeightClasses = 'h-10 md:h-11';
  const buttonBaseClasses = `${buttonHeightClasses} px-3 md:px-4 lg:px-5 rounded-full font-medium transition-all duration-300 ease-in-out flex items-center justify-center shadow-[0_0_15px_rgba(164,93,232,0.2)] text-center`;
  const primaryButtonClasses = `${buttonBaseClasses} oracle-wave hover:shadow-[0_0_25px_rgba(164,93,232,0.65)] text-white ${ctaIdle ? 'animate-oracle-glow' : ''}`;
  const secondaryButtonClasses = `${buttonBaseClasses} border border-[#A45DE8]/60 bg-[#070219]/70 hover:border-[#D4B6FF] hover:bg-[#6A3683]/20 text-[#FBFBFE]`;
  const iconButtonClasses = `${buttonHeightClasses} w-11 rounded-full border border-[#6A3683]/50 bg-[#070219]/80 flex items-center justify-center text-[#FBFBFE] shadow-[0_8px_18px_rgba(7,2,25,0.4)] hover:border-[#D4B6FF] transition-colors`;
  const ctaButtonRef = useRef(null);

  const renderLogo = (width = 40, height = 40) => (
    <Link href={`/${locale}`} className="relative group">
      <Image
        src="/images/asktheoraclelogo.svg"
        alt="Ask the Oracle logo - free yes/no oracle reading"
        width={width}
        height={height}
        priority
        placeholder="blur"
        blurDataURL="/images/asktheoraclelogo.svg"
        className="h-8 md:h-10 w-auto"
      />
    </Link>
  );

  const renderDesktopNav = () => (
    <div className={navWrapperClasses}>
      <div className={navPillClasses}>
      <nav className="flex items-center">
          <ul className={navListClasses}>
          <li className="flex items-center">
            <Link href={`/${locale}`} className={navLinkClasses} title={t('nav.yesNoOracle')}>
              <span className="hidden lg:inline">{t('nav.yesNoOracle')}</span>
              <span className="lg:hidden">{t('nav.yesNoOracleShort')}</span>
            </Link>
          </li>
          <li className="flex items-center">
            <Link href={`/${locale}/temple/oracle-readings/pendulum-yes-or-no`} className={navLinkClasses} title={t('nav.pendulumReading')}>
              <span className="hidden lg:inline">{t('nav.pendulumReading')}</span>
              <span className="lg:hidden">{t('nav.pendulumReadingShort')}</span>
            </Link>
          </li>
          <li className="flex items-center">
            <Link href={`/${locale}/temple/oracle-readings/ai-dream-interpreter`} className={navLinkClasses} title={t('nav.dreamInterpreter')}>
              <span className="hidden lg:inline">{t('nav.dreamInterpreter')}</span>
              <span className="lg:hidden">{t('nav.dreamInterpreterShort')}</span>
            </Link>
          </li>
          <li className="flex items-center">
            <Link href={`/${locale}/temple/oracle-readings/oracle-card-reading`} className={navLinkClasses} title={t('nav.oracleCards')}>
              <span className="hidden lg:inline">{t('nav.oracleCards')}</span>
              <span className="lg:hidden">{t('nav.oracleCardsShort')}</span>
            </Link>
          </li>
          <li className="flex items-center">
            <div className="h-6 w-px bg-[#6A3683]/40"></div>
          </li>
          <li className="flex items-center">
            <Link href={`/${locale}/pricing`} className={navLinkClasses}>
              {t('nav.pricing')}
            </Link>
          </li>
          <li className="relative flex items-center">
            <OracleWisdomsDropDown
              navLinkClasses={navLinkClasses}
              isMobileNavOpen={isMobileNavOpen}
              toggleMobileNav={() => setIsMobileNavOpen(false)}
              label={t('nav.more')}
              locale={locale}
            />
          </li>
        </ul>
      </nav>
      </div>
    </div>
  );

  const renderDesktopActions = () => (
    <div className="hidden items-center space-x-1 md:space-x-1.5 lg:space-x-2 md:flex">
      <button
        type="button"
        onClick={handleSignIn}
        disabled={isProcessingSignIn}
        className={`${loginLinkClasses} ${isProcessingSignIn ? 'cursor-wait opacity-80' : ''}`}
      >
        {isProcessingSignIn ? (
          <span className="inline-flex items-center gap-2">
            <Spinner />
            <span>{t('buttons.loggingIn')}</span>
          </span>
        ) : (
          t('buttons.login')
        )}
      </button>
      <LanguageSwitcher className={iconButtonClasses} />
      <div className="h-6 w-px bg-[#6A3683]/40" />
      <Button
        variant="primary"
        onClick={async () => {
          if (ctaButtonRef.current) {
            ctaButtonRef.current.blur();
          }
          
          // If user is not logged in, authenticate first
          if (!userSessionId) {
            setIsProcessingSignIn(true);
            try {
              const result = await authenticateAndCreateSession(refreshCSRF);
              
              if (result.success && result.user) {
                router.push(`/${locale}/temple`);
                setIsProcessingSignIn(false);
                return;
              } else if (!result.user && !result.error) {
                // Redirect flow initiated (mobile)
                setIsProcessingSignIn(false);
                return;
              } else if (result.error) {
                if (result.error.code === 'popup-closed') {
                  console.log('Sign-in popup closed by user.');
                } else if (result.error.code === 'popup-cancelled') {
                  console.log('Sign-in popup request cancelled.');
                } else if (result.error.code === 'redirect-cancelled') {
                  console.log('Sign-in redirect operation was cancelled or failed.');
                } else {
                  console.error('Error during authentication:', result.error);
                  alert(result.error.message || 'An error occurred during the sign-in process. Please try again.');
                }
                setIsProcessingSignIn(false);
                return;
              }
            } catch (error) {
              console.error('Unexpected error during sign-in:', error);
              alert('An error occurred during the sign-in process. Please try again.');
              setIsProcessingSignIn(false);
              return;
            }
          } else {
            // User is already logged in, redirect to temple
            router.push(`/${locale}/temple`);
          }
        }}
        disabled={premiumLoading || isProcessingSignIn}
        className={`${primaryButtonClasses} ${isMobileNavOpen ? 'pointer-events-none opacity-50' : ''}`}
        ref={ctaButtonRef}
        onMouseEnter={() => setCtaIdle(false)}
        onFocus={() => setCtaIdle(false)}
      >
        <span className="font-semibold tracking-wide text-xs sm:text-sm md:text-sm lg:text-base xl:text-lg drop-shadow-[0_1px_3px_rgba(21,0,43,0.45)] whitespace-nowrap">
          {isProcessingSignIn ? (
            <span className="inline-flex items-center gap-2">
              <Spinner />
              <span>{t('buttons.loggingIn')}</span>
            </span>
          ) : (
            t('buttons.chat')
          )}
        </span>
      </Button>
    </div>
  );

  const renderMobileNav = () => (
    !onToggleSidebar && isMobileNavOpen && (
      <div
        id="mobile-nav"
        ref={mobileNavRef}
        className="fixed inset-x-0 top-0 z-60 min-h-screen bg-black/60 backdrop-blur-sm md:hidden"
        role="dialog"
        aria-modal="true"
        aria-label={mobileNavLabel}
      >
        <div className="flex min-h-screen flex-col justify-end">
          <div className="mx-auto mt-16 w-full max-w-md flex-1 px-4">
            <button
              type="button"
              className="ml-auto flex h-10 w-10 items-center justify-center rounded-full border border-[#6A3683]/50 bg-[#070219]/80 text-[#FBFBFE] shadow-[0_12px_30px_rgba(7,2,25,0.45)]"
              onClick={closeMobileNav}
            >
              <X size={22} />
              <span className="sr-only">{t('nav.close')}</span>
            </button>
          </div>

          <div className="relative rounded-t-3xl border-t border-[#6A3683]/40 bg-[#050316]/95 px-6 pb-12 pt-8 shadow-[0_-12px_40px_rgba(7,2,25,0.55)]" tabIndex={-1}>
            <div className="mx-auto mb-6 h-1.5 w-16 rounded-full bg-[#6A3683]/60" />
            <nav className="space-y-4 text-center" aria-label={mobileNavLabel}>
          <Link
            href={`/${locale}`}
                className="block rounded-full border border-transparent bg-[#070219]/80 px-6 py-3 text-base font-medium text-[#FBFBFE] transition-colors hover:border-[#6A3683]/40 hover:bg-[#6A3683]/20"
                onClick={closeMobileNav}
          >
            {t('nav.yesNoOracle')}
          </Link>
          <Link
            href={`/${locale}/temple/oracle-readings/pendulum-yes-or-no`}
                className="block rounded-full border border-transparent bg-[#070219]/80 px-6 py-3 text-base font-medium text-[#FBFBFE] transition-colors hover:border-[#6A3683]/40 hover:bg-[#6A3683]/20"
                onClick={closeMobileNav}
          >
            {t('nav.pendulumReading')}
          </Link>
          <Link
            href={`/${locale}/temple/oracle-readings/ai-dream-interpreter`}
                className="block rounded-full border border-transparent bg-[#070219]/80 px-6 py-3 text-base font-medium text-[#FBFBFE] transition-colors hover:border-[#6A3683]/40 hover:bg-[#6A3683]/20"
                onClick={closeMobileNav}
          >
            {t('nav.dreamInterpreter')}
          </Link>
          <Link
            href={`/${locale}/temple/oracle-readings/oracle-card-reading`}
                className="block rounded-full border border-transparent bg-[#070219]/80 px-6 py-3 text-base font-medium text-[#FBFBFE] transition-colors hover:border-[#6A3683]/40 hover:bg-[#6A3683]/20"
                onClick={closeMobileNav}
          >
            {t('nav.oracleCards')}
          </Link>
          <Link
            href={`/${locale}/#faq`}
                className="block rounded-full border border-transparent bg-[#070219]/80 px-6 py-3 text-base font-medium text-[#FBFBFE] transition-colors hover:border-[#6A3683]/40 hover:bg-[#6A3683]/20"
                onClick={closeMobileNav}
          >
            {t('nav.faq')}
          </Link>
          <Link
            href={`/${locale}/pricing`}
                className="block rounded-full border border-transparent bg-[#070219]/80 px-6 py-3 text-base font-medium text-[#FBFBFE] transition-colors hover:border-[#6A3683]/40 hover:bg-[#6A3683]/20"
                onClick={closeMobileNav}
          >
            {t('nav.pricing')}
          </Link>
          <OracleWisdomsDropDown
                navLinkClasses="block rounded-full border border-transparent bg-[#070219]/80 px-6 py-3 text-base font-medium text-[#FBFBFE] transition-colors hover:border-[#6A3683]/40 hover:bg-[#6A3683]/20"
            isMobileNavOpen={isMobileNavOpen}
                toggleMobileNav={closeMobileNav}
            label={t('nav.more')}
            locale={locale}
          />
              <div className="flex items-center justify-center">
                <LanguageSwitcher className="rounded-full border border-[#6A3683]/50 bg-[#070219]/80 px-4 py-2" isMobile />
              </div>
            </nav>

            <div className="mt-8 grid gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              handleSignIn();
                  closeMobileNav();
            }}
            disabled={isProcessingSignIn}
                className="w-full rounded-full border border-[#6A3683]/60 bg-[#070219]/80 py-3 text-base"
          >
            {isProcessingSignIn ? (
              <span className="flex items-center justify-center">
              <Spinner />
                <span className="ml-2">{t('buttons.loggingIn')}</span>
              </span>
            ) : (
              t('buttons.login')
            )}
          </Button>

          <Button
            variant="primary"
            onClick={async () => {
              closeMobileNav();
              
              // If user is not logged in, authenticate first
              if (!userSessionId) {
                setIsProcessingSignIn(true);
                try {
                  const result = await authenticateAndCreateSession(refreshCSRF);
                  
                  if (result.success && result.user) {
                    router.push(`/${locale}/temple`);
                    setIsProcessingSignIn(false);
                    return;
                  } else if (!result.user && !result.error) {
                    // Redirect flow initiated (mobile)
                    setIsProcessingSignIn(false);
                    return;
                  } else if (result.error) {
                    if (result.error.code === 'popup-closed') {
                      console.log('Sign-in popup closed by user.');
                    } else if (result.error.code === 'popup-cancelled') {
                      console.log('Sign-in popup request cancelled.');
                    } else if (result.error.code === 'redirect-cancelled') {
                      console.log('Sign-in redirect operation was cancelled or failed.');
                    } else {
                      console.error('Error during authentication:', result.error);
                      alert(result.error.message || 'An error occurred during the sign-in process. Please try again.');
                    }
                    setIsProcessingSignIn(false);
                    return;
                  }
                } catch (error) {
                  console.error('Unexpected error during sign-in:', error);
                  alert('An error occurred during the sign-in process. Please try again.');
                  setIsProcessingSignIn(false);
                  return;
                }
              } else {
                // User is already logged in, redirect to temple
                router.push(`/${locale}/temple`);
              }
            }}
            disabled={premiumLoading || isProcessingSignIn}
                className="w-full rounded-full oracle-wave py-3 text-sm md:text-base animate-oracle-glow"
          >
            <span className="font-semibold tracking-wide drop-shadow-[0_1px_3px_rgba(21,0,43,0.45)]">
              {isProcessingSignIn ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner />
                  <span>{t('buttons.loggingIn')}</span>
                </span>
              ) : (
                t('buttons.chat')
              )}
            </span>
          </Button>
            </div>
          </div>
        </div>
      </div>
    )
  );

  const renderFloatingCTA = () => {
    if (userSessionId || !isFloatingCTAVisible) {
      return null;
    }

    return (
      <div className="pointer-events-none fixed inset-x-0 bottom-6 z-55 px-4 md:hidden">
        <Button
          variant="primary"
          onClick={async () => {
            setCtaIdle(false);
            
            // If user is not logged in, authenticate first
            if (!userSessionId) {
              setIsProcessingSignIn(true);
              try {
                const result = await authenticateAndCreateSession(refreshCSRF);
                
                if (result.success && result.user) {
                  router.push(`/${locale}/temple`);
                  setIsProcessingSignIn(false);
                  return;
                } else if (!result.user && !result.error) {
                  // Redirect flow initiated (mobile)
                  setIsProcessingSignIn(false);
                  return;
                } else if (result.error) {
                  if (result.error.code === 'popup-closed') {
                    console.log('Sign-in popup closed by user.');
                  } else if (result.error.code === 'popup-cancelled') {
                    console.log('Sign-in popup request cancelled.');
                  } else if (result.error.code === 'redirect-cancelled') {
                    console.log('Sign-in redirect operation was cancelled or failed.');
                  } else {
                    console.error('Error during authentication:', result.error);
                    alert(result.error.message || 'An error occurred during the sign-in process. Please try again.');
                  }
                  setIsProcessingSignIn(false);
                  return;
                }
              } catch (error) {
                console.error('Unexpected error during sign-in:', error);
                alert('An error occurred during the sign-in process. Please try again.');
                setIsProcessingSignIn(false);
                return;
              }
            } else {
              // User is already logged in, redirect to temple
              router.push(`/${locale}/temple`);
            }
          }}
          disabled={isProcessingSignIn}
          className={`pointer-events-auto w-full rounded-full px-4 md:px-6 py-3 text-sm md:text-base shadow-[0_24px_48px_rgba(164,93,232,0.45)] oracle-wave ${ctaIdle ? 'animate-oracle-glow' : ''}`}
          aria-label={t('buttons.chat')}
        >
          <span className="font-semibold tracking-wide drop-shadow-[0_1px_3px_rgba(21,0,43,0.45)] whitespace-nowrap">
            {isProcessingSignIn ? (
              <span className="inline-flex items-center gap-2">
                <Spinner />
                <span>{t('buttons.loggingIn')}</span>
              </span>
            ) : (
              t('buttons.chat')
            )}
          </span>
        </Button>
      </div>
    );
  };

  if (!userSessionId) {
    return (
      <>
        <header className={`${baseHeaderClasses} ${className || ''}`}>
          <div className={containerClasses}>
            <div className="mr-1 md:mr-2 shrink-0">{renderLogo(48, 48)}</div>
            {renderDesktopNav()}
            <div className="ml-auto flex items-center gap-2 md:gap-3">
              {renderDesktopActions()}
              <button
                type="button"
                onClick={handleMobileMenuClick}
                className={`md:hidden flex h-11 w-11 items-center justify-center rounded-full border border-[#6A3683]/50 bg-[#070219]/80 text-[#FBFBFE] transition-colors hover:border-[#D4B6FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A45DE8] ${isMobileNavOpen ? 'border-[#A45DE8]' : ''}`}
                aria-label={isMobileNavOpen ? t('buttons.close') : t('buttons.openMenu')}
                aria-expanded={isMobileNavOpen}
                aria-controls="mobile-nav"
              >
                {isMobileNavOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
              {renderMobileNav()}
            </div>
          </div>
        </header>
        {/* {renderFloatingCTA()} */}
      </>
    );
  }

  const renderDesktopNavLoggedIn = () => (
    <div className="hidden md:flex md:ml-1 lg:ml-2">
      <div className={navPillClasses}>
        <nav className="flex items-center">
          <ul className={navListClasses}>
            <li className="flex items-center">
              <Link href={`/${locale}`} className={navLinkClasses} title={t('nav.yesNoOracle')}>
                <span className="hidden lg:inline">{t('nav.yesNoOracle')}</span>
                <span className="lg:hidden">{t('nav.yesNoOracleShort')}</span>
              </Link>
            </li>
            <li className="flex items-center">
              <Link href={`/${locale}/temple/oracle-readings/pendulum-yes-or-no`} className={navLinkClasses} title={t('nav.pendulumReading')}>
                <span className="hidden lg:inline">{t('nav.pendulumReading')}</span>
                <span className="lg:hidden">{t('nav.pendulumReadingShort')}</span>
              </Link>
            </li>
            <li className="flex items-center">
              <Link href={`/${locale}/temple/oracle-readings/ai-dream-interpreter`} className={navLinkClasses} title={t('nav.dreamInterpreter')}>
                <span className="hidden lg:inline">{t('nav.dreamInterpreter')}</span>
                <span className="lg:hidden">{t('nav.dreamInterpreterShort')}</span>
              </Link>
            </li>
            <li className="flex items-center">
              <Link href={`/${locale}/temple/oracle-readings/oracle-card-reading`} className={navLinkClasses} title={t('nav.oracleCards')}>
                <span className="hidden lg:inline">{t('nav.oracleCards')}</span>
                <span className="lg:hidden">{t('nav.oracleCardsShort')}</span>
              </Link>
            </li>
            {isPremium && !premiumLoading && (
              <li className="flex items-center">
                <Link href={`/${locale}/temple/oracle-readings/chatinterface`} className={navLinkClasses} title={t('nav.lunasChat')}>
                  <span className="hidden lg:inline">{t('nav.lunasChat')}</span>
                  <span className="lg:hidden">{t('nav.lunasChatShort')}</span>
                </Link>
              </li>
            )}
            <li className="flex items-center">
              <div className="h-6 w-px bg-[#6A3683]/40"></div>
            </li>
            <li className="relative flex items-center">
              <OracleWisdomsDropDown
                navLinkClasses={navLinkClasses}
                isMobileNavOpen={isMobileNavOpen}
                toggleMobileNav={() => setIsMobileNavOpen(false)}
                label={t('nav.more')}
                locale={locale}
              />
            </li>
            <li className="flex items-center">
              <div className="h-6 w-px bg-[#6A3683]/40"></div>
            </li>
            <li className="flex items-center">
              <Link href={`/${locale}/temple`} className={navLinkClasses}>
                {t('nav.temple')}
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );

  const renderDesktopActionsLoggedIn = () => (
    <div className="hidden items-center space-x-3 md:flex">
      <LanguageSwitcher className={iconButtonClasses} />
      <div className="h-6 w-px bg-[#6A3683]/40" />
      {userSessionId && <NotificationBell />}
      {userSessionId && <div className="h-6 w-px bg-[#6A3683]/40" />}
      {firebaseUser && (
        <div className="relative flex items-center">
          <button
            type="button"
            onClick={() => setIsAvatarMenuOpen((open) => !open)}
            className="group flex items-center space-x-2 rounded-full border border-[#A45DE8]/60 bg-[#070219]/80 px-3 py-1 shadow-[0_8px_18px_rgba(7,2,25,0.4)] transition-colors hover:border-[#D4B6FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A45DE8]"
            aria-haspopup="menu"
            aria-expanded={isAvatarMenuOpen}
          >
            <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full">
              <Image
                src={firebaseUser.photoURL ?? '/images/avatar-placeholder.png'}
                alt={avatarDisplayName ? `${avatarDisplayName} avatar` : 'User avatar'}
                width={36}
                height={36}
                className="h-9 w-9 rounded-full object-cover"
                priority={false}
              />
            </div>
            <ChevronDown className={`h-4 w-4 text-[#D4B6FF]/70 transition-transform ${isAvatarMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {isAvatarMenuOpen && (
            <div
              ref={avatarMenuRef}
              className="absolute right-0 top-full z-50 mt-2 w-56 rounded-2xl border border-[#6A3683]/40 bg-[#050316]/95 p-3 shadow-[0_20px_45px_rgba(7,2,25,0.5)] backdrop-blur"
              role="menu"
              aria-label={accountMenuLabel}
            >
              <div className="mb-2 flex items-center space-x-3 rounded-xl border border-[#6A3683]/30 bg-[#0b061e]/80 p-3">
                <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-[#A45DE8]/40">
                  <Image
                    src={firebaseUser.photoURL ?? '/images/avatar-placeholder.png'}
                    alt={avatarDisplayName ? `${avatarDisplayName} avatar` : 'User avatar'}
                    width={40}
                    height={40}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                </div>
                <div className="truncate">
                  <p className="text-sm font-semibold text-[#FBFBFE]">
                    {avatarDisplayName}
                  </p>
                  {avatarEmail ? (
                    <p className="text-xs text-[#D4B6FF]/70 truncate">
                      {avatarEmail}
                    </p>
                  ) : null}
                </div>
              </div>

              <nav className="space-y-1" role="none">
                <button
                  type="button"
                  className="flex w-full items-center space-x-3 rounded-xl px-3 py-2 text-sm text-left text-[#FBFBFE] transition-colors hover:bg-[#6A3683]/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A45DE8]"
                  onClick={() => {
                    setIsAvatarMenuOpen(false);
                    router.push(`/${locale}/temple/profile`);
                  }}
                  role="menuitem"
                >
                  <UserCircle2 className="h-4 w-4 text-[#D4B6FF]" />
                  <span>{accountProfileLabel}</span>
                </button>

                <button
                  type="button"
                  className="flex w-full items-center space-x-3 rounded-xl px-3 py-2 text-sm text-left text-[#FBFBFE] transition-colors hover:bg-[#6A3683]/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A45DE8]"
                  onClick={() => {
                    setIsAvatarMenuOpen(false);
                    router.push(`/${locale}/temple/journeytracker/readingshistory`);
                  }}
                  role="menuitem"
                >
                  <span>{accountHistoryLabel}</span>
                </button>

                <div className="h-px bg-[#6A3683]/40" role="separator" />

                <button
                  type="button"
                  className="flex w-full items-center space-x-3 rounded-xl px-3 py-2 text-sm text-left text-[#FBFBFE] transition-colors hover:bg-[#6A3683]/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A45DE8]"
                  onClick={() => {
                    setIsAvatarMenuOpen(false);
                    handleSignOut();
                  }}
                  role="menuitem"
                >
                  <LogOut className="h-4 w-4 text-[#A45DE8]" />
                  <span>{accountLogoutLabel}</span>
                </button>
              </nav>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderMobileNavLoggedIn = () => (
    !onToggleSidebar && isMobileNavOpen && (
      <div className="absolute top-full right-4 mt-2 w-56 rounded-md shadow-lg bg-[#050316] ring-1 ring-[#6A3683]/50 focus:outline-none z-50 origin-top-right md:hidden">
        <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
          <Link
            href={`/${locale}`}
            className={`${navLinkClasses} block px-4 py-2 text-sm`}
            onClick={() => setIsMobileNavOpen(false)}
            role="menuitem"
          >
            {t('nav.yesNoOracle')}
          </Link>
          <Link
            href={`/${locale}/temple/oracle-readings/pendulum-yes-or-no`}
            className={`${navLinkClasses} block px-4 py-2 text-sm`}
            onClick={() => setIsMobileNavOpen(false)}
            role="menuitem"
          >
            {t('nav.pendulumReading')}
          </Link>
          <Link
            href={`/${locale}/temple/oracle-readings/ai-dream-interpreter`}
            className={`${navLinkClasses} block px-4 py-2 text-sm`}
            onClick={() => setIsMobileNavOpen(false)}
            role="menuitem"
          >
            {t('nav.dreamInterpreter')}
          </Link>
          <Link
            href={`/${locale}/temple/oracle-readings/oracle-card-reading`}
            className={`${navLinkClasses} block px-4 py-2 text-sm`}
            onClick={() => setIsMobileNavOpen(false)}
            role="menuitem"
          >
            {t('nav.oracleCards')}
          </Link>
          {isPremium && !premiumLoading && (
            <Link
              href={`/${locale}/temple/oracle-readings/chatinterface`}
              className={`${navLinkClasses} block px-4 py-2 text-sm`}
              onClick={() => setIsMobileNavOpen(false)}
              role="menuitem"
            >
              {t('nav.lunasChat')}
            </Link>
          )}
          <OracleWisdomsDropDown
            navLinkClasses={`${navLinkClasses} block px-4 py-2 text-sm`}
            isMobileNavOpen={isMobileNavOpen}
            toggleMobileNav={() => setIsMobileNavOpen(false)}
            label={t('nav.more')}
            locale={locale}
          />
          <div className="h-px bg-[#6A3683]/30 my-1 mx-2" />
          <Link
            href={`/${locale}/temple`}
            className={`${navLinkClasses} block px-4 py-2 text-sm`}
            onClick={() => setIsMobileNavOpen(false)}
            role="menuitem"
          >
            {t('nav.temple')}
          </Link>
          <div className="h-px bg-[#6A3683]/30 my-1 mx-2" />
          <LanguageSwitcher className="w-full" isMobile />
          <div className="h-px bg-[#6A3683]/30 my-1 mx-2" />
          <Button
            variant="secondary"
            onClick={() => {
              handleSignOut();
              setIsMobileNavOpen(false);
            }}
            disabled={isProcessingSignOut}
            className={`${secondaryButtonClasses} w-full`}
          >
            {isProcessingSignOut ? (
              <span className="flex items.CENTER justify-center">
                <Spinner />
                <span className="ml-2">{t('buttons.signingOut')}</span>
              </span>
            ) : (
              t('buttons.signOut')
            )}
          </Button>
        </div>
      </div>
    )
  );

  return (
    <>
      <header className={`${baseHeaderClasses} ${className || ''}`}>
        <div className={containerClasses}>
          <div className="mr-1 shrink-0 md:mr-2">{renderLogo(188, 40)}</div>
          {renderDesktopNavLoggedIn()}
          <div className="ml-auto flex items-center gap-2 md:gap-3">
            {renderDesktopActionsLoggedIn()}
            <button
              type="button"
              onClick={handleMobileMenuClick}
              className={`md:hidden flex h-11 w-11 items-center justify-center rounded-full border border-[#6A3683]/50 bg-[#070219]/80 text-[#FBFBFE] transition-colors hover:border-[#D4B6FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A45DE8] ${isMobileNavOpen ? 'border-[#A45DE8]' : ''}`}
              aria-label={isMobileNavOpen ? t('buttons.close') : t('buttons.openMenu')}
              aria-expanded={isMobileNavOpen}
              aria-controls="mobile-nav"
            >
              {isMobileNavOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            {renderMobileNavLoggedIn()}
          </div>
        </div>
      </header>
      {/* {renderFloatingCTA()} */}
    </>
  );
}